import { z } from 'zod';
import { IsoTimestamp, UserId } from './common';

/**
 * Subscription tiers governing per-user feature gating.
 *  - `free_beta` is the default during the OutLinked beta window — every
 *    user is auto-granted Pro-equivalent caps without payment until the
 *    beta-launch global flag is flipped.
 *  - `trial` is the $1 single-cycle taste-test — 1 cycle, 1 submission,
 *    24h TTL. Paid via Apple IAP non-renewing consumable. Email-locked
 *    via TRIAL_USED# rows so a card holder can't trial twice from the
 *    same address (survives account delete via the tombstone pattern).
 *  - `solo` and `pro` are the recurring paid SKUs delivered via Apple
 *    IAP / Play Billing through RevenueCat.
 */
export const ENTITLEMENT_TIERS = ['free_beta', 'trial', 'solo', 'pro'] as const;

/** Forward-compat read variant — see #482. */
export const EntitlementTierReadableSchema = z
  .enum([...ENTITLEMENT_TIERS, 'unknown'] as const)
  .catch('unknown');
export type EntitlementTierReadable = z.infer<typeof EntitlementTierReadableSchema>;
export type EntitlementTier = (typeof ENTITLEMENT_TIERS)[number];

export const EntitlementTierSchema = z.enum(ENTITLEMENT_TIERS);

/**
 * Supported LLM model identifiers — six curated rungs covering the
 * cheap-to-strong range. The router (apps/api/src/lib/model-router.ts)
 * picks per call based on the call site, question type, retry
 * confidence, and cost-cap proximity. Tier no longer gates which models
 * are reachable — every user gets the full ladder, and per-user spend
 * is bounded by the daily cost cap rather than a model allowlist.
 */
export const TIER_MODELS = [
  'gpt-5.4-nano',
  'claude-haiku-4-5',
  'gpt-5.4-mini',
  'claude-sonnet-4-6',
  'gpt-5.5',
  'claude-opus-4-8',
] as const;
export type TierModel = (typeof TIER_MODELS)[number];

export interface TierCaps {
  appsPerMonth: number;
  /**
   * Hard ceiling on automatic run cycles per UTC day. Was 12 on free_beta/pro
   * during the scaffold pass; current product policy is 4 across every tier
   * so usage patterns stay legible during beta. Adjustable via TIER_CAPS.
   */
  runsPerDay: number;
  /**
   * Hard ceiling on individual run wall-clock minutes. Worker self-enforces:
   * each cycle checks `now - run.startedAt` and halts gracefully past this.
   * 60 min covers a typical batch + a wide margin; adjustable as we see
   * usage patterns.
   */
  maxRunMinutes: number;
  maxPairedDevices: number;
}

export const TIER_CAPS: Record<EntitlementTier, TierCaps> = {
  free_beta: {
    appsPerMonth: 1000,
    runsPerDay: 4,
    // Lowered 60 → 30 (#379) to keep beta usage patterns legible while
    // enforcement landed: a 30-min wall-clock ceiling means each free-beta
    // run produces at most one "natural" applicator burst before a halt,
    // making it easier to read the apply / fail / needs_attention mix per
    // cycle. Enforcement lives in run-cycle.ts via isTimeCapReached().
    maxRunMinutes: 30,
    maxPairedDevices: 2,
  },
  trial: {
    // $1 single-cycle taste-test. The user sees a real submitted job — same
    // worker pipeline, same answer-bank, same browser — capped at exactly
    // one application so the unit cost stays well below the $1 collected.
    // Email-locked to prevent multi-trial abuse: a TRIAL_USED# row is
    // written on purchase and checked on every fresh signup, surviving
    // account delete via the same tombstone pattern as users-delete.ts.
    appsPerMonth: 1,
    runsPerDay: 1,
    maxRunMinutes: 30,
    maxPairedDevices: 1,
  },
  solo: {
    appsPerMonth: 300,
    // Solo is the entry tier — 1 cycle/day is enough to keep a daily search
    // healthy without burning Solo budget on multiple low-yield re-scans.
    // Pro lifts this to 4/day for users running across multiple candidate
    // profiles or large title sets.
    runsPerDay: 1,
    // Solo also gets a 30-min run ceiling vs Pro's 60. Forces tighter title /
    // location filters at the Solo budget; a Solo cycle that needs 60 minutes
    // of wall-clock is almost certainly hitting jobs that won't convert at
    // their salary floor anyway.
    maxRunMinutes: 30,
    maxPairedDevices: 1,
  },
  pro: {
    appsPerMonth: 1000,
    runsPerDay: 4,
    maxRunMinutes: 60,
    maxPairedDevices: 2,
  },
};

/**
 * Full entitlement row as stored under `PK=USER#{userId}, SK=ENTITLEMENT`.
 *
 * `currentPeriodAppsApplied` is reset by `resetCurrentPeriod` when the
 * current month rolls over relative to `currentPeriodStartedAt`. `runsToday`
 * is reset whenever `runsTodayResetAt` falls into a previous UTC day.
 *
 * `inBetaOverride` is the per-user opt-out from beta-free mode. When the
 * global billing-mode is `beta_free` we normally short-circuit to the
 * `free_beta` tier; setting this flag forces the user back onto their
 * actual entitlement row (used by Adam to validate the paid flow before
 * the global switch).
 */
export const Entitlement = z.object({
  userId: UserId,
  tier: EntitlementTierReadableSchema,
  productId: z.string().optional(),
  expiresAt: z.number().int().optional(),
  currentPeriodAppsApplied: z.number().int().nonnegative().default(0),
  currentPeriodStartedAt: z.number().int().optional(),
  runsToday: z.number().int().nonnegative().default(0),
  runsTodayResetAt: z.number().int().optional(),
  lastWebhookEventId: z.string().optional(),
  inBetaOverride: z.boolean().default(false),
  /**
   * True once the user has purchased the $0.99 24-hour trial. Prevents
   * multi-trial abuse — the client-side paywall hides the "Start Trial"
   * CTA when this is true. Not decremented on trial expiry: the trial
   * is a once-per-account entitlement, not a resettable state. See
   * docs/business/pricing-tiers.md §trial for the abuse-defense rationale.
   */
  trialUsed: z.boolean().default(false),
  /**
   * #709 — Per-user opt-out of the daily LLM cost cap. When true, the
   * API bypasses `enforceCostCapBefore` and `readCostCapStatus` returns
   * `{used: 0, cap: Infinity}` (which suppresses the mobile "Daily AI
   * budget exhausted" banner). Mirrors `inBetaOverride`: flipped via
   * direct DDB write for staff / support / heavy-tester exemptions on
   * paid tiers. The `free_beta` tier already has a blanket bypass; this
   * field lets a paid-tier user opt out without changing tier. Default
   * false — additive schema, no migration required for pre-#709 rows.
   */
  costCapBypass: z.boolean().default(false),
  updatedAt: IsoTimestamp,
});
export type Entitlement = z.infer<typeof Entitlement>;

export const EntitlementSchema = Entitlement;

export const UpdateEntitlementRequest = Entitlement.omit({
  userId: true,
  updatedAt: true,
})
  .extend({ tier: EntitlementTierSchema })
  .partial();
export type UpdateEntitlementRequest = z.infer<typeof UpdateEntitlementRequest>;

/**
 * #451 — Response for `POST /v1/entitlement/gate-check`, the worker's
 * cycle-start entitlement resolution. Pre-#451 the desktop worker called
 * `featureGates.canRun` etc. directly against DynamoDB via the AWS SDK,
 * which silently failed with "Could not load credentials from any
 * providers" (desktop has no AWS creds by design per CLAUDE.md — "the
 * cloud holds NO LinkedIn passwords... and NO automation IP space").
 * The fail-open catch then set the wall-clock cap to 60 min instead of
 * the tier's actual value (30 for free_beta / solo / trial), letting
 * runs blow past the intended ceiling. This shape mirrors what the
 * worker previously computed from `TIER_CAPS[gate.tier].maxRunMinutes`
 * + `featureGates.getAllowedModels`.
 *
 * Server-side handler ALSO increments `runsToday` atomically when
 * `allowed=true` — same shape as the old `featureGates.incrementRunStart`
 * that the worker used to call directly. Combined into one round-trip
 * so cycle-start latency doesn't compound.
 */
export const GateCheckResponse = z.object({
  tier: EntitlementTierReadableSchema,
  allowed: z.boolean(),
  /** Populated when `allowed=false` — e.g. "daily cap of 4 runs reached for free_beta tier". */
  reason: z.string().optional(),
  /** Set of provider@model tokens allowed by the current tier. */
  allowedModels: z.array(z.string()),
  /** Per-tier wall-clock ceiling for this cycle, in minutes. Enforced client-side. */
  maxRunMinutes: z.number().int().positive(),
});
export type GateCheckResponse = z.infer<typeof GateCheckResponse>;

/**
 * RevenueCat webhook event envelope. We model only the fields we actually
 * read; RevenueCat includes a lot more (subscriber attributes, store
 * metadata, original transaction id, etc.) that we currently ignore.
 *
 * `event.environment` is `'PRODUCTION'` or `'SANDBOX'`. We accept both but
 * the handler logs the sandbox path so we notice mis-routed test events.
 */
export const IapWebhookEventType = z.enum([
  'INITIAL_PURCHASE',
  'RENEWAL',
  'CANCELLATION',
  'EXPIRATION',
  'BILLING_ISSUE',
  'PRODUCT_CHANGE',
  'TRANSFER',
  // Tolerate other types — RevenueCat may add new ones; we ignore unknowns
  // gracefully rather than throwing.
  'UNCATEGORIZED',
]);
export type IapWebhookEventType = z.infer<typeof IapWebhookEventType>;

export const IapWebhookEvent = z.object({
  api_version: z.string().optional(),
  event: z.object({
    id: z.string().min(1),
    type: IapWebhookEventType.or(z.string()),
    app_user_id: z.string().min(1),
    product_id: z.string().min(1),
    expiration_at_ms: z.number().int().nullable().optional(),
    environment: z.enum(['PRODUCTION', 'SANDBOX']).optional(),
    // RC also sends a server-side event timestamp — we don't currently
    // store it but accept it so the parse doesn't fail on a stray field.
    event_timestamp_ms: z.number().int().optional(),
  }),
});
export type IapWebhookEvent = z.infer<typeof IapWebhookEvent>;

/**
 * Global billing-mode record stored at `PK=CONFIG#GLOBAL, SK=BILLING_MODE`.
 * The default for a fresh deploy is `beta_free` — paid tier enforcement only
 * kicks in after Adam flips this manually.
 */
export const BillingMode = z.object({
  mode: z.enum(['beta_free', 'live']),
  betaLaunchDate: z.string(),
  betaCutoffDate: z.string(),
  userCountThreshold: z.number().int().positive(),
  notes: z.string().optional(),
  updatedAt: IsoTimestamp,
});
export type BillingMode = z.infer<typeof BillingMode>;

export const UpdateBillingModeRequest = z.object({
  mode: z.enum(['beta_free', 'live']),
  notes: z.string().max(500).optional(),
});
export type UpdateBillingModeRequest = z.infer<typeof UpdateBillingModeRequest>;

/**
 * User-facing entitlement view returned by `GET /v1/users/me/entitlement`.
 * Combines the entitlement row with derived display fields (current-period
 * end timestamp, beta-mode flags) so the mobile + desktop UIs don't have to
 * recompute them client-side.
 */
export const EntitlementView = z.object({
  tier: EntitlementTierReadableSchema,
  expiresAt: z.number().int().optional(),
  currentPeriodAppsApplied: z.number().int().nonnegative(),
  currentPeriodEndsAt: IsoTimestamp,
  caps: z.object({
    appsPerMonth: z.number().int(),
    runsPerDay: z.number().int(),
    // Defaulted to 60 so a client built against the new schema doesn't
    // ZodError when an older API instance is still returning the
    // pre-maxRunMinutes shape (mobile + API may not ship in lockstep).
    // Once every active API deployment is on the new handler this can
    // tighten back to `z.number().int().positive()`.
    maxRunMinutes: z.number().int().positive().default(60),
    maxPairedDevices: z.number().int(),
  }),
  betaMode: z.boolean(),
  betaCutoffDate: z.string(),
  isLegacyBetaUser: z.boolean(),
  /**
   * True once this user has consumed their $0.99 24-hour trial. Client
   * uses this to hide the "Start Trial" CTA on the paywall — a repeat
   * trial attempt would be caught server-side, but hiding the button is
   * a friendlier UX than a purchase-time error. Defaulted so older API
   * instances that haven't shipped this field yet don't break the
   * client's zod parse.
   */
  trialUsed: z.boolean().default(false),
});
export type EntitlementView = z.infer<typeof EntitlementView>;

/**
 * Per-user beta-override patch body sent by `/v1/admin/users/:userId/beta-override`.
 */
export const SetBetaOverrideRequest = z.object({
  inBetaOverride: z.boolean(),
});
export type SetBetaOverrideRequest = z.infer<typeof SetBetaOverrideRequest>;
